import { useMemo, useState } from 'react';
import { useNow } from './mailHooks';
import { C, S, btn } from './mailStyles';
import { emailsLabel, monogram, monogramColor, plural, relativeTime } from './mailUtils';

const MAX_SENDERS = 30;

function normalizeSenders(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const s of raw) {
    if (!s || typeof s.sender_id !== 'string' || !s.sender_id || seen.has(s.sender_id)) continue;
    seen.add(s.sender_id);
    const address = typeof s.address === 'string' ? s.address : '';
    const name = (typeof s.name === 'string' && s.name.trim()) || address || 'Unknown sender';
    const count = Number(s.count);
    out.push({
      id: s.sender_id,
      name,
      address,
      count: Number.isFinite(count) && count > 0 ? Math.floor(count) : 0,
      last: s.last_received_at,
      manual: s.method === 'manual',
      selectedDefault: s.selected_default !== false,
    });
    if (out.length >= MAX_SENDERS) break;
  }
  return out;
}

function MethodBadge({ manual }) {
  return (
    <span style={{
      ...S.chip,
      background: manual ? '#FFF4E5' : '#E8F6EE',
      color: manual ? C.orange : C.green,
    }}
    >
      {manual ? 'Manual' : 'One-tap'}
    </span>
  );
}

function SenderRow({ sender, checked, disabled, now, onToggle }) {
  return (
    <label
      data-testid="mail-picker-row"
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: `1px solid ${C.border}`,
        cursor: disabled ? 'default' : 'pointer', minWidth: 0,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={() => onToggle(sender.id)}
        aria-label={sender.name}
        style={{ width: 18, height: 18, flexShrink: 0, accentColor: C.accent, margin: 0 }}
      />
      <div style={{
        width: 32, height: 32, borderRadius: 16, background: monogramColor(sender.name), color: '#fff', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
      }}
      >
        {monogram(sender.name)}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ ...S.ellipsis, fontSize: 14, fontWeight: 600 }} title={sender.address || undefined}>{sender.name}</span>
        <span style={{ ...S.ellipsis, fontSize: 12, color: C.textDim }}>
          {[emailsLabel(sender.count), relativeTime(sender.last, now)].filter(Boolean).join(' · ')}
        </span>
      </div>
      <MethodBadge manual={sender.manual} />
    </label>
  );
}

export default function MailUnsubscribePickerCard({ data, sendFollowUp }) {
  const now = useNow(60000);
  const senders = useMemo(() => normalizeSenders(data?.senders), [data?.senders]);
  const [selected, setSelected] = useState(() => new Set(senders.filter((s) => s.selectedDefault).map((s) => s.id)));
  const [archive, setArchive] = useState(true);
  const [tapped, setTapped] = useState(false);

  const windowDays = Number(data?.window_days) || 0;
  const totalEmails = Number(data?.total_emails) || 0;
  const scanId = typeof data?.scan_id === 'string' ? data.scan_id : '';
  const chosen = senders.filter((s) => selected.has(s.id));
  const n = chosen.length;
  const archiveSum = chosen.reduce((sum, s) => sum + s.count, 0);
  const label = archive
    ? `Unsubscribe ${n.toLocaleString()} · Archive ${archiveSum.toLocaleString()}`
    : `Unsubscribe ${n.toLocaleString()}`;
  const disabled = tapped || n === 0 || !scanId;

  const toggle = (id) => {
    if (tapped) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const onSubmit = () => {
    if (disabled) return;
    setTapped(true);
    sendFollowUp('unsubscribe', `Unsubscribe ${plural(n, 'sender')}`, {
      mail_action: 'unsubscribe', scan_id: scanId, sender_ids: chosen.map((s) => s.id), archive,
    });
  };

  if (senders.length === 0) {
    return (
      <div style={S.card} data-testid="mail-picker-empty">
        <p style={S.sub}>No promotional senders found in the last {plural(windowDays, 'day')}.</p>
      </div>
    );
  }

  return (
    <div style={S.card} data-testid="mail-unsubscribe-picker">
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.45 }} data-testid="mail-picker-header">
        Found {plural(senders.length, 'sender')} behind most of your promotions — {emailsLabel(totalEmails)} in the
        last {plural(windowDays, 'day')}. All selected; uncheck any you want to keep:
      </p>
      <div style={{ maxHeight: 340, overflowY: 'auto', marginTop: -4 }}>
        {senders.map((s) => (
          <SenderRow key={s.id} sender={s} checked={selected.has(s.id)} disabled={tapped} now={now} onToggle={toggle} />
        ))}
      </div>
      <div style={{
        position: 'sticky', bottom: 0, background: C.bg, display: 'flex', flexDirection: 'column', gap: 10,
        paddingTop: 10, borderTop: `1px solid ${C.border}`,
      }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Also archive past emails</div>
            <div style={{ fontSize: 12, color: C.textDim }}>Recoverable from All Mail in Gmail</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={archive}
            aria-label="Also archive past emails"
            disabled={tapped}
            onClick={() => setArchive((v) => !v)}
            style={btn({
              width: 44, height: 26, borderRadius: 13, border: 'none', padding: 2, flexShrink: 0, cursor: 'pointer',
              background: archive ? C.green : C.border, display: 'flex', justifyContent: archive ? 'flex-end' : 'flex-start',
              transition: 'background 0.2s',
            }, tapped)}
          >
            <span style={{ width: 22, height: 22, borderRadius: 11, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.25)' }} />
          </button>
        </div>
        <button
          type="button"
          data-testid="mail-picker-submit"
          style={btn({ ...S.btnPrimary, flex: 'none', width: '100%' }, disabled)}
          disabled={disabled}
          onClick={onSubmit}
        >
          {label}
        </button>
        {tapped && <p style={{ ...S.sub, textAlign: 'center' }}>Sent — check the confirmation below</p>}
      </div>
    </div>
  );
}
